// src/pages/PrivacyPolicy.jsx
import React from 'react';

export default function PrivacyPolicy() {
  return (
    <div className="max-w-2xl mx-auto p-6 text-gray-800">
      <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>
      <p className="mb-2">
        <strong>Last updated: June 2025</strong>
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Introduction</h2>
      <p className="mb-4">
        Welcome to BrainyBuddy!
        We care deeply about your privacy. This policy explains what personal data we collect, how we use it, and your choices regarding your information. We are committed to protecting the privacy and safety of students, parents, and teachers who use our platform.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">1. What Data We Collect</h2>
      <ul className="list-disc pl-6 mb-4">
        <li>
          <strong>Account Information:</strong> Name, email address, password, role (student/parent/teacher). For students, we may ask for your age to ensure safe and appropriate experiences.
        </li>
        <li>
          <strong>Learning Data:</strong> Chat messages with BuddyBot, quiz results, game scores, learning progress.
        </li>
        <li>
          <strong>Usage Information:</strong> Device type, browser, general location (not precise), and usage analytics.
        </li>
        <li>
          <strong>Voice Data:</strong> If you use the microphone feature, your spoken words are transcribed for conversation. <u>We do not store or share your raw voice recordings.</u>
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">2. How We Use Your Data</h2>
      <ul className="list-disc pl-6 mb-4">
        <li>To provide and improve learning features, games, and the BuddyBot experience.</li>
        <li>To personalize learning content and advice based on your role (student, parent, or teacher).</li>
        <li>To communicate updates, improvements, or important information.</li>
        <li>For research and analytics to enhance our platform (always anonymized).</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">3. Who Can See Your Data</h2>
      <ul className="list-disc pl-6 mb-4">
        <li>
          <strong>Only you</strong> and your authorized guardians/teachers can view your private chat history, scores, and progress.
        </li>
        <li>
          <strong>We do not sell or share</strong> your personal information with third parties for advertising or marketing.
        </li>
        <li>
          We may share aggregated, anonymized data (never linked to you personally) for research or product improvement.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">4. Your Rights &amp; Choices</h2>
      <ul className="list-disc pl-6 mb-4">
        <li>
          <strong>Access:</strong> You can see and review your data within your account.
        </li>
        <li>
          <strong>Correction:</strong> You can correct your personal information at any time.
        </li>
        <li>
          <strong>Deletion:</strong> You can request deletion of your account and all personal data (contact us at <a href="mailto:support@brainybuddy.in" className="underline text-blue-600">support@brainybuddy.in</a>).
        </li>
        <li>
          <strong>Consent (for children):</strong> If you are under 13, a parent/guardian’s consent is required to create an account (in accordance with COPPA and similar regulations).
        </li>
        <li>
          <strong>Export:</strong> You can request a copy of your data in a machine-readable format.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">5. Security</h2>
      <p className="mb-4">
        We use secure technologies and best practices to protect your information.
        All data is encrypted in transit and at rest.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">6. Cookies &amp; Tracking</h2>
      <ul className="list-disc pl-6 mb-4">
        <li>We use essential cookies to keep you logged in and help the platform work properly.</li>
        <li>We may use analytics cookies (always anonymized) to improve the user experience.</li>
        <li>You can manage cookie preferences in your browser.</li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">7. Changes to This Policy</h2>
      <p className="mb-4">
        We may update this policy as our platform evolves. We will always notify you (via the app or email) before major changes.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">8. Contact Us</h2>
      <p>
        If you have questions or concerns about your privacy, please contact us at: <a href="mailto:support@brainybuddy.in" className="underline text-blue-600">support@brainybuddy.in</a>
      </p>

      <p className="mt-6">
        By using BrainyBuddy, you agree to this privacy policy. Thank you for trusting us with your learning journey!
      </p>
    </div>
  );
}
